import React, {FC, useEffect, useRef, useState} from 'react';
import Map from '@geomatico/geocomponents/Map/Map';
import maplibregl, {StyleSpecification} from 'maplibre-gl';
import {cogProtocol, colorScale, setColorFunction} from '@geomatico/maplibre-cog-protocol';
import ColorRampLegend from '@geomatico/geocomponents/Map/ColorRampLegend';
import {SxProps} from '@mui/system';
import {Layer, MapRef, Source} from 'react-map-gl/maplibre';
import ValueTooltip from '../../components/ValueTooltip';
import TemperatureFormatter from '../../components/TemperatureFormatter';
import ArrayChart from '../../components/ArrayChart';

const INITIAL_VIEWPORT = {
  latitude: 37.387617,
  longitude: 9.338379,
  zoom: 4.2,
  bearing: 0,
  pitch: 0
};

const legendSx: SxProps = {
  position: 'absolute',
  bottom: 24,
  left: 24,
  width: 600
};

const layers: StyleSpecification['layers'] = [];
const sources: StyleSpecification['sources'] = {};

maplibregl.addProtocol('cog', cogProtocol);
const cogUrl = 'https://cdn.geomatico.es/MAP_SSTY_val_cog_timeseries.tif';

type Interpolator = (value: number) => [number, number, number];

type State = {
  band: number,
  interpolator: Interpolator
};

const showBand = (band: number, interpolator: Interpolator) => {

  setColorFunction(cogUrl, (pixel, color, metadata) => {
    const value = pixel[band];
    if (value === 0) {
      color[3] = 0;
    } else {
      const [r, g, b] = interpolator(value * metadata.scale + metadata.offset);
      color[0] = r;
      color[1] = g;
      color[2] = b;
      color[3] = 224;
    }
  });
};

export type MainContentProps = {
  mapStyle: string,
  colorRamp: string,
  year: number
};

const MainContent: FC<MainContentProps> = ({mapStyle, colorRamp, year}) => {
  const [viewport, setViewport] = useState(INITIAL_VIEWPORT);
  const [state, setState] = useState<State>({band: year - 1982, interpolator: () => [255, 255, 255]});
  const [pixel, setPixel] = useState<Array<number>>(new Array(2020-1982).fill(NaN));

  const mapRef = useRef<MapRef>(null);

  useEffect(() => {
    const band = year - 1982;
    const interpolator = colorScale({colorScheme: colorRamp, min: 12, max: 24, isContinuous: true, isReverse: true});
    setState({band, interpolator});
    showBand(band, interpolator);
  }, [colorRamp, year]);

  return <>
    <Map
      ref={mapRef}
      mapStyle={mapStyle}
      viewport={viewport}
      onViewportChange={setViewport}
      sources={sources}
      layers={layers}
    >
      <Source type="raster" id="source" tileSize={256} url={`cog://${cogUrl}#kk${year}-${colorRamp}`}/>
      <Layer id="layer" type="raster" source="source" beforeId="contour-250m"/>
      <ValueTooltip mapRef={mapRef} url={cogUrl} formatter={TemperatureFormatter} onPixel={setPixel} band={state.band}/>
    </Map>
    <ArrayChart data={pixel} i={state.band} interpolateColor={state.interpolator}/>
    <ColorRampLegend colorScheme={colorRamp} sx={legendSx} domain={[12, 24]} reverse/>
  </>;
};

export default MainContent;
